import { FormControl, Grid, IconButton, Link, ListItem, MenuItem, Select, Typography, makeStyles} from "@material-ui/core";
import { AllEditorialTypes, EditorialTemplate, EditorialType } from "../../domain/domain";
import { useEffect, useState, useRef } from "react";
import { createEditorialTemplate, deleteEditorialTemplate, getEditorialTemplates, updateEditorialTemplate, updateEditorialTemplates} from "../../api/editorial";
import { useDispatch, useSelector } from "react-redux";
import { EditorialTemplatesActionType } from "../../reducers/editorialTemplates.reducer";
import { ApplicationState } from "../../reducers";
import { getEditorialContentTypeName } from "../../utils";
import ClearIcon from "@material-ui/icons/Clear";
import PencilIcon from "@material-ui/icons/Edit";
import ReorderIcon from "@material-ui/icons/Reorder";
import CloseIcon from "@material-ui/icons/Close";
import AddEditorialTemplateDialog from "./AddEditorialTemplateDialog";
import { UiActionType } from "../../reducers/ui.reducer";
import { ConfirmationDialog } from "../Dialogs/ConfirmationDialog";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";
import { reorder } from "../ComponentManagementPage/utils";

  const useStyles = makeStyles((theme) => ({
    draggingListItem: {
      background: "rgb(235,235,235)",
    },
    selectedItem: {
      backgroundColor: "#e0e0e0",
    },
    listContainer: {
      maxHeight: "70vh",
      overflowY: "auto",
      paddingRight: theme.spacing(1),
      overflowX: "hidden",
      width: "100%",
      scrollbarGutter: "stable",
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-track": {
        background: "#f1f1f1",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#888",
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: "#555",
      },
    },
    gridContainer: {
      width: "100%",
      margin: 0,
      display: "flex",
      flexDirection: "column",
    },
    listItem: {
      marginBottom: theme.spacing(1),
      marginRight: "5px",
      marginLeft: "5px",
      padding: theme.spacing(1),
      transition: "all 0.2s ease",
      width: "100%",
      maxWidth: "280px",
      boxSizing: "border-box",
      flexShrink: 0,
      border: "1px solid #e0e0e0",
      "&:hover": {
        backgroundColor: "rgb(235,235,235)",
      },
      "&:focus": {
        backgroundColor: "rgb(224,224,224)",
        outline: "2px solid rgba(0, 0, 0, 0.2)",
        boxShadow: "none",
      },
      overflow: "hidden",
    },
    itemContent: {
      display: "flex",
      alignItems: "center",
      width: "100%",
      minWidth: 0,
    },
    itemText: {
      flex: "1 1 auto",
      minWidth: 0,
      marginRight: theme.spacing(1),
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "normal",
      overflowWrap: "break-word",
      "& h3": {
        margin: 0,
        padding: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
    actionButtons: {
      flex: "0 0 auto",
      display: "flex",
      gap: theme.spacing(1),
      "& button": {
        minWidth: "32px",
        padding: "4px",
      },
    },
    moveButtons: {
      flex: "0 0 auto",
      display: "flex",
      flexDirection: "row",
      gap: "2px",
      marginLeft: theme.spacing(1),
      "& button": {
        minWidth: "24px",
        height: "24px",
        padding: "0",
      },
      "& button[aria-disabled='true']": {
        opacity: 0.6,
        cursor: "not-allowed",
        color: "#949494",
        "& svg": {
          color: "#949494",
        },
        "&:focus": {
          outline: "2px solid #000000",
          boxShadow: "none"
        }
      },
    },
  }));
  
  const EditorialTemplatesList: React.FC = () => {
    const [selectedEditorialType, setSelectedEditorialType] =
      useState<EditorialType>(AllEditorialTypes[0]);
    const dispatch = useDispatch();
    const editorialTemplates = useSelector(
      (app: ApplicationState) => app.editorialTemplates.editorialTemplates
    );
    const selectedEditorialTemplate = useSelector(
      (app: ApplicationState) => app.editorialTemplates.selectedEditorialTemplate
    );
    const [addEditorialTemplateDialogOpen, setAddEditorialTemplateDialogOpen] =
      useState<boolean>(false);
    const [
      deleteEditorialConfirmationDialogOpen,
      setDeleteEditorialConfirmationDialogOpen,
    ] = useState<boolean>(false);
    const [
      editorialTemplateDialogEditorialTemplate,
      setEditorialTemplateDialogEditorialTemplate,
    ] = useState<EditorialTemplate | undefined>(undefined);
    const [addDialogMode, setAddDialogMode] = useState<"add" | "edit">("add");
    const [reorderingItemId, setReorderingItemId] = useState<number | null>(null);
    const reorderButtonRef = useRef<HTMLButtonElement | null>(null);
    const classes = useStyles();
  
    useEffect(() => {
      getEditorialTemplates(selectedEditorialType)
        .then((response) => {
          const orderedTemplates = [...response.data].sort((a, b) => 
            a.templateOrder - b.templateOrder
          );
          
          dispatch({
            type: EditorialTemplatesActionType.EDITORIAL_TEMPLATES_LOADED,
            editorialTemplates: orderedTemplates,
          });
          if (orderedTemplates.length > 0) {
            dispatch({
              type: EditorialTemplatesActionType.SELECTED_EDITORIAL_CHANGED,
              newSelectedEditorialTemplate: orderedTemplates[0],
            });
          } else {
            dispatch({
              type: EditorialTemplatesActionType.SELECTED_EDITORIAL_CHANGED,
              newSelectedEditorialTemplate: undefined,
            });
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }, [selectedEditorialType]);
  
    if (editorialTemplates === undefined) {
      return <></>;
    }

    // Function to announce messages for screen readers
    const announceForScreenReader = (message: string) => {
      const announcer = document.createElement('div');
      announcer.setAttribute('aria-live', 'assertive');
      announcer.setAttribute('role', 'status');
      announcer.style.position = 'absolute';
      announcer.style.width = '1px';
      announcer.style.height = '1px';
      announcer.style.overflow = 'hidden';
      announcer.style.clip = 'rect(0, 0, 0, 0)';
      document.body.appendChild(announcer);
      
      setTimeout(() => {
        announcer.textContent = message;
        setTimeout(() => {
          document.body.removeChild(announcer);
        }, 3000);
      }, 100);
    };
    const announcementPosition = (index: number, title: string, moveDirection: "up" | "down") => {
      const position = index + 1;
      const newPosition = moveDirection === "up" ? position - 1 : position + 1;
      return `You have moved the item ${title} from position ${position} to position ${newPosition}`;
    }
    const handleMoveUp = (index: number) => {
      if (index === 0) return;
      const newItems = [...sortedEditorialTemplates];
      const movedItem = newItems[index];
      [newItems[index], newItems[index - 1]] = [
        newItems[index - 1],
        newItems[index],
      ];
      newItems.forEach((item, i) => (item.templateOrder = i));
      dispatch({
        type: EditorialTemplatesActionType.EDITORIAL_TEMPLATES_LOADED,
        editorialTemplates: newItems,
      });
      updateOrder(newItems);
      // Announce position change to screen readers
      announceForScreenReader(announcementPosition(index, movedItem.title, "up"));
    };
  
    const handleMoveDown = (index: number) => {
      if (index === sortedEditorialTemplates.length - 1) return;
      const newItems = [...sortedEditorialTemplates];
      const movedItem = newItems[index];
      [newItems[index], newItems[index + 1]] = [
        newItems[index + 1],
        newItems[index],
      ];
      newItems.forEach((item, i) => (item.templateOrder = i));
      dispatch({
        type: EditorialTemplatesActionType.EDITORIAL_TEMPLATES_LOADED,
        editorialTemplates: newItems,
      });
      updateOrder(newItems);
      // Announce position change to screen readers
      announceForScreenReader(announcementPosition(index, movedItem.title, "down"));
    };
  
    const updateOrder = (items: EditorialTemplate[]) => {
      updateEditorialTemplates(items)
        .then((response) => {
          dispatch({
            type: UiActionType.SNACKBAR_STATE_CHANGED,
            newState: {
              open: true,
              message: "Order updated",
              severity: "success",
            },
          });
        })
        .catch((error) => {
          dispatch({
            type: UiActionType.SNACKBAR_STATE_CHANGED,
            newState: {
              open: true,
              message: "Could not update order",
              severity: "error",
            },
          });
          console.error(error);
        });
    };

    let sortedEditorialTemplates = [...editorialTemplates];
    sortedEditorialTemplates.sort((e1, e2) =>
      e1.templateOrder < e2.templateOrder ? -1 : 1
    );

    const selectAriaLabel = selectedEditorialTemplate === undefined ? `${getEditorialContentTypeName(selectedEditorialType)} No editorial templates found for selected editorial type` : getEditorialContentTypeName(selectedEditorialType);

    const onDragEnd = ({ destination, source }: DropResult) => {
      if (!destination) {
        // dropped outside the list
        return;
      }
      const newItems = reorder(sortedEditorialTemplates, source.index, destination.index);
      for (let i = 0; i < newItems.length; i++) {
        newItems[i].templateOrder = i;
      }
      dispatch({
        type: EditorialTemplatesActionType.EDITORIAL_TEMPLATES_LOADED,
        editorialTemplates: newItems,
      });
      updateEditorialTemplates(newItems)
        .then((response) => {
          dispatch({
            type: UiActionType.SNACKBAR_STATE_CHANGED,
            newState: {
              open: true,
              message: "Order updated",
              severity: "success",
            },
          });
        })
        .catch((error) => {
          dispatch({
            type: UiActionType.SNACKBAR_STATE_CHANGED,
            newState: {
              open: true,
              message: "Could not update order",
              severity: "error",
            },
          });
          console.error(error);
        });
    };

    return (
      <>
        <AddEditorialTemplateDialog
          open={addEditorialTemplateDialogOpen}
          editorialTemplate={editorialTemplateDialogEditorialTemplate}
          mode={addDialogMode}
          onOK={(title: string) => {
            setAddEditorialTemplateDialogOpen(false);
            let newEditorialTemplate: EditorialTemplate = {
              id: 0,
              title: title,
              content: "",
              editorialType: selectedEditorialType,
              templateOrder: 0,
            };
            if (addDialogMode === "add") {
              createEditorialTemplate(newEditorialTemplate)
                .then((response) => {
                  dispatch({
                    type: UiActionType.SNACKBAR_STATE_CHANGED,
                    newState: {
                      open: true,
                      message: "Editorial Template created",
                      severity: "success",
                    },
                  });
                  let updatedEditorials = [response.data, ...editorialTemplates];
                  for (let i = 0; i < updatedEditorials.length; i++) {
                    updatedEditorials[i].templateOrder = i;
                  }
                  dispatch({
                    type: EditorialTemplatesActionType.EDITORIAL_TEMPLATES_LOADED,
                    editorialTemplates: updatedEditorials,
                  });
                  dispatch({
                    type: EditorialTemplatesActionType.SELECTED_EDITORIAL_CHANGED,
                    newSelectedEditorialTemplate: response.data,
                  });
                })
                .catch((error) => {
                  dispatch({
                    type: UiActionType.SNACKBAR_STATE_CHANGED,
                    newState: {
                      open: true,
                      message: "Could not create editorial template",
                      severity: "error",
                    },
                  });
                  console.error(error);
                });
            } else {
              if (editorialTemplateDialogEditorialTemplate === undefined) {
                return;
              }
              updateEditorialTemplate({
                ...editorialTemplateDialogEditorialTemplate,
                title: title,
              })
                .then((response) => {
                  dispatch({
                    type: UiActionType.SNACKBAR_STATE_CHANGED,
                    newState: {
                      open: true,
                      message: "Editorial Template updated",
                      severity: "success",
                    },
                  });
                  dispatch({
                    type: EditorialTemplatesActionType.EDITORIAL_TEMPLATE_UPDATED,
                    editorialTemplate: response.data,
                  });
                })
                .catch((error) => {
                  dispatch({
                    type: UiActionType.SNACKBAR_STATE_CHANGED,
                    newState: {
                      open: true,
                      message: "Could not update editorial template",
                      severity: "error",
                    },
                  });
                  console.error(error);
                });
            }
          }}
          onCancel={() => {
            setAddEditorialTemplateDialogOpen(false);
          }}
        />
        <ConfirmationDialog
          open={deleteEditorialConfirmationDialogOpen}
          title=""
          contentText="Are you sure you want to delete this editorial template?"
          handleNo={() => {
            setDeleteEditorialConfirmationDialogOpen(false);
          }}
          handleYes={() => {
            if (selectedEditorialTemplate === undefined) {
              return;
            }
            setDeleteEditorialConfirmationDialogOpen(false);
            deleteEditorialTemplate(selectedEditorialTemplate)
              .then((response) => {
                dispatch({
                  type: UiActionType.SNACKBAR_STATE_CHANGED,
                  newState: {
                    open: true,
                    message: "Editorial Template deleted",
                    severity: "success",
                  },
                });
                dispatch({
                  type: EditorialTemplatesActionType.EDITORIAL_TEMPLATE_DELETED,
                  editorialTemplate: selectedEditorialTemplate,
                });
              })
              .catch((error) => {
                dispatch({
                  type: UiActionType.SNACKBAR_STATE_CHANGED,
                  newState: {
                    open: true,
                    message: "Could not delete editorial template",
                    severity: "error",
                  },
                });
                console.error(error);
              });
          }}
        />
        <Grid
          container
          direction="column"
          spacing={2}
          className={classes.gridContainer}
        >
          <Grid item>
            <FormControl size="small" style={{ width: "100%" }}>
              <Select
                variant="outlined"
                value={selectedEditorialType}
                onChange={(event) => {
                  setSelectedEditorialType(event.target.value as EditorialType);
                }}
                inputProps={{
                  'aria-label': selectAriaLabel,
                  'aria-describedby': selectedEditorialTemplate === undefined ? 'no-templates-alert-message' : undefined,
                }}
              >
                {AllEditorialTypes.map((editorialType) => {
                  return (
                    <MenuItem value={editorialType}>
                      {getEditorialContentTypeName(editorialType)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Link
              type="button"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setAddDialogMode("add");
                setEditorialTemplateDialogEditorialTemplate(undefined);
                setAddEditorialTemplateDialogOpen(true);
              }}
            >
              Add editorial template
            </Link>
          </Grid>
          <Grid item>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable-templates-list">
                {(provided) => (
                  <div 
                    ref={provided.innerRef} 
                    {...provided.droppableProps}
                    className={classes.listContainer}
                  >
                    {sortedEditorialTemplates.map((item, index) => (
                      <Draggable
                         draggableId={"" + item.id} 
                         index={item.templateOrder} 
                         key={item.id}
                      >
                        {(dragProvided, snapshot) => (
                          <ListItem
                            ref={dragProvided.innerRef}
                            button
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            aria-describedby=""
                            className={`${classes.listItem} ${snapshot.isDragging ? classes.draggingListItem : ""} ${
                              selectedEditorialTemplate?.id === item.id ? classes.selectedItem : ""
                            }`}
                            onKeyDown={(e) => {
                              if (
                                e.key === "Enter" &&
                                document.activeElement === e.currentTarget
                              ) {
                                e.preventDefault();
                                e.stopPropagation();
                                dispatch({
                                  type: EditorialTemplatesActionType.SELECTED_EDITORIAL_CHANGED,
                                  newSelectedEditorialTemplate: item,
                                });
                              }
                            }}
                            onClick={() => {
                                dispatch({ type: EditorialTemplatesActionType.SELECTED_EDITORIAL_CHANGED, newSelectedEditorialTemplate: item});
                            }}
                          > <Grid container direction='row' wrap='nowrap' spacing={0} alignItems='center' className={classes.itemContent}>
                              <Grid item style={{flexGrow: 1}} title={item.title} className={classes.itemText}>
                                 {item.title}
                              </Grid>
                              {reorderingItemId === item.id ? (
                                <>
                                  <Grid item className={classes.moveButtons}>
                                    <IconButton
                                      size="small"
                                      aria-disabled={index === 0 ? "true" : "false"}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (index !== 0) {
                                          handleMoveUp(index);
                                        }
                                      }}
                                      aria-label="Move up"
                                    >
                                      <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
  
                                    <IconButton
                                      size="small"
                                      aria-disabled={index === sortedEditorialTemplates.length - 1 ? "true" : "false"}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (index !== sortedEditorialTemplates.length - 1) {
                                          handleMoveDown(index);
                                        }
                                      }}
                                      aria-label="Move down"
                                    >
                                      <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
  
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setReorderingItemId(null);
                                        if (reorderButtonRef.current) {
                                          reorderButtonRef.current.focus();
                                        }
                                      }}
                                      aria-label="Close reordering"
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Grid>
                                </>
                              ) : (
                                <>
                                  <Grid item className={classes.actionButtons}>
                                    <IconButton
                                      size="small"
                                      ref={item.id === reorderingItemId ? reorderButtonRef : undefined}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        reorderButtonRef.current = e.currentTarget;
                                        setReorderingItemId(item.id);
                                      }}
                                      aria-label={`Reorder ${item.title}`}
                                    >
                                      <ReorderIcon fontSize="small" />
                                    </IconButton>
  
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setAddDialogMode("edit");
                                        setEditorialTemplateDialogEditorialTemplate(item);
                                        setAddEditorialTemplateDialogOpen(true);
                                      }}
                                      aria-label={`Edit ${item.title}`}
                                    >
                                      <PencilIcon fontSize="small" />
                                    </IconButton>
  
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        dispatch({
                                          type: EditorialTemplatesActionType.SELECTED_EDITORIAL_CHANGED,
                                          newSelectedEditorialTemplate: item,
                                        });
                                        setDeleteEditorialConfirmationDialogOpen(true);
                                      }}
                                      aria-label={`Delete ${item.title}`}
                                    >
                                      <ClearIcon fontSize="small" />
                                    </IconButton>
                                  </Grid>
                                </>
                              )}
                            </Grid>
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Grid>
          <Grid item>
            <Typography style={{fontStyle: 'italic'}}>
              Drag and drop to reorder items.
            </Typography>
          </Grid>
        </Grid>
      </>
    );
  };
  
  export default EditorialTemplatesList;